// src/pages/provider/ProfileImageProviderServicePage.jsx
// (Rename from BusinessVerificationPage.jsx)

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  X,
  Trash2,
  Image as ImageIcon,
  Loader2,
  CloudUpload,
  User,
  Camera
} from 'lucide-react';

import colors from '../../constants/colors';
import useTranslation from '../../hooks/useTranslation';
import styles from '../../styles/ProfileImageProvider.module.css';

import { selectUser, updateUser } from '../../store/slices/authSlice';

import {
  uploadProfileImage,
  uploadCoverImage,
  selectImageUploading,
  selectUploadProgress,
  selectUploadSuccess,
  selectUploadError,
  resetUploadState,
  clearImageErrors,
} from '../../store/slices/providerImageSlice';

import { updateCurrentProvider } from '../../store/slices/providerSlice';
import { selectProfile, getProfile } from '../../store/slices/profileSlice';

const ProfileImageProviderServicePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const user = useSelector(selectUser);
  const profile = useSelector(selectProfile);
  const isImageUploading = useSelector(selectImageUploading);
  const uploadProgress = useSelector(selectUploadProgress);
  const uploadSuccessRedux = useSelector(selectUploadSuccess);
  const uploadErrorRedux = useSelector(selectUploadError);

  const [showImageOptionsModal, setShowImageOptionsModal] = useState(false);
  const [uploadingImageType, setUploadingImageType] = useState(null);
  const [imageUploadError, setImageUploadError] = useState(null);
  const [imageUploadSuccess, setImageUploadSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [validationMessage, setValidationMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [localProfileImage, setLocalProfileImage] = useState(null);
  const [localCoverImage, setLocalCoverImage] = useState(null);

  const imageFileInputRef = useRef(null);
  const initialLoadDone = useRef(false);
  const isUpdatingFromUpload = useRef(false);
  const isImageUploadInProgress = useRef(false);

  // Get provider ID from profile or user
  const providerId = profile?.providerProfile?._id || 
                     user?.providerProfile?._id || 
                     user?.providerId;

  // Translations
  const { translatedText: profileImageTitle } = useTranslation('Profile Image');
  const { translatedText: coverImageTitle } = useTranslation('Cover Image');
  const { translatedText: uploadProfilePhoto } = useTranslation('Upload Profile Photo');
  const { translatedText: uploadCoverPhoto } = useTranslation('Upload Cover Photo');
  const { translatedText: changePhoto } = useTranslation('Change Photo');
  const { translatedText: removePhoto } = useTranslation('Remove Photo');
  const { translatedText: uploading } = useTranslation('Uploading...');
  const { translatedText: uploadSuccessMessage } = useTranslation('Image uploaded successfully');
  const { translatedText: deleteConfirm } = useTranslation('Are you sure you want to remove this image?');
  const { translatedText: deleteSuccessMessage } = useTranslation('Image removed successfully');
  const { translatedText: skip } = useTranslation('Skip');
  const { translatedText: saving } = useTranslation('Saving...');
  const { translatedText: cancel } = useTranslation('Cancel');
  const { translatedText: chooseFromGallery } = useTranslation('Choose from Gallery');

  // Reset image upload state on mount
  useEffect(() => {
    dispatch(resetUploadState());
    dispatch(clearImageErrors());
    setImageUploadError(null);
    setImageUploadSuccess(false);

    return () => {
      dispatch(resetUploadState());
      dispatch(clearImageErrors());
    };
  }, [dispatch]);

  // Fetch profile if not available
  useEffect(() => {
    const loadProfile = async () => {
      if (user?._id && !profile) {
        try {
          await dispatch(getProfile()).unwrap();
        } catch (error) {
          console.error('Failed to fetch profile:', error);
        }
      }
    };
    loadProfile();
  }, [dispatch, user, profile]);

  // Initialize images from profile or user
  useEffect(() => {
    if (!user && !profile) return;

    // Get profile image from profile first, then fallback to user
    const profileImgUrl = profile?.providerProfile?.profileImage?.url ||
                          user?.providerProfile?.profileImage?.url ||
                          user?.profileImage ||
                          null;

    const coverImgUrl = profile?.providerProfile?.coverImage?.url ||
                        user?.providerProfile?.coverImage?.url ||
                        user?.coverImage ||
                        null;

    setLocalProfileImage(profileImgUrl);
    setLocalCoverImage(coverImgUrl);
    setIsLoading(false);
    initialLoadDone.current = true;

    console.log('📸 Profile image from profile:', profileImgUrl);
    console.log('📸 Profile image from user:', user?.profileImage);
    console.log('📸 Full profile data:', profile);

  }, [user, profile]);

  // Handle image upload success
  useEffect(() => {
    if (uploadSuccessRedux && !isImageUploading) {
      setImageUploadSuccess(true);
      setImageUploadError(null);

      const timer = setTimeout(() => {
        setImageUploadSuccess(false);
        dispatch(resetUploadState());
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [uploadSuccessRedux, isImageUploading, dispatch]);

  // Handle image upload error
  useEffect(() => {
    if (uploadErrorRedux && !isImageUploading) {
      setImageUploadError(uploadErrorRedux);
      setImageUploadSuccess(false);

      const timer = setTimeout(() => {
        setImageUploadError(null);
        dispatch(clearImageErrors());
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [uploadErrorRedux, isImageUploading, dispatch]);

  // Navigate away if no providerId and not loading
  useEffect(() => {
    if (!isLoading && !providerId) {
      navigate('/complete-provider');
    }
  }, [isLoading, providerId, navigate]);

  const handleSelectImage = (type) => {
    setUploadingImageType(type);
    setShowImageOptionsModal(true);
  };

  const handleImageFileSelect = (type, event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    isImageUploadInProgress.current = true;
    setValidationMessage(null);

    const objectUrl = URL.createObjectURL(file);

    if (type === 'profile') {
      setLocalProfileImage(objectUrl);
    } else {
      setLocalCoverImage(objectUrl);
    }

    handleImageUpload(type, file);

    event.target.value = '';
    setShowImageOptionsModal(false);
  };

  const handleImageUpload = async (type, file) => {
    if (!providerId) {
      alert('Provider ID not found');
      setUploadingImageType(null);
      isImageUploadInProgress.current = false;
      return;
    }

    try {
      setImageUploadError(null);
      setImageUploadSuccess(false);
      isUpdatingFromUpload.current = true;

      let result;
      if (type === 'profile') {
        result = await dispatch(uploadProfileImage({
          providerId,
          file,
          options: { width: 800, height: 800 }
        }));
      } else {
        result = await dispatch(uploadCoverImage({
          providerId,
          file,
          options: { width: 1200, height: 400 }
        }));
      }

      if (result && !result.error) {
        const imageData = result.payload?.data;
        const imageUrl = imageData?.url;

        if (imageUrl) {
          const imageObject = {
            url: imageUrl,
            publicId: imageData?.publicId || null,
            uploadedAt: new Date().toISOString()
          };

          if (type === 'profile' && imageData?.thumbnail) {
            imageObject.thumbnail = imageData.thumbnail;
          }

          // Update local state first
          if (type === 'profile') {
            setLocalProfileImage(imageUrl);
          } else {
            setLocalCoverImage(imageUrl);
          }

          // Get current provider profile from either profile or user
          const currentProviderProfile = profile?.providerProfile || user?.providerProfile || {};

          // Preserve existing images
          const existingProfileImage = currentProviderProfile.profileImage || 
                                       (profile?.providerProfile?.profileImage) ||
                                       (user?.providerProfile?.profileImage) ||
                                       { url: user?.profileImage || null };
          
          const existingCoverImage = currentProviderProfile.coverImage || 
                                     (profile?.providerProfile?.coverImage) ||
                                     (user?.providerProfile?.coverImage) ||
                                     { url: user?.coverImage || null };

          // Build provider updates carefully preserving both images
          const providerUpdates = {
            providerProfile: {
              ...currentProviderProfile,
            }
          };

          if (type === 'profile') {
            providerUpdates.providerProfile.profileImage = imageObject;
            providerUpdates.profileImage = imageUrl;
            // Preserve cover image
            providerUpdates.providerProfile.coverImage = existingCoverImage;
            if (existingCoverImage?.url) {
              providerUpdates.coverImage = existingCoverImage.url;
            }
          } else {
            providerUpdates.providerProfile.coverImage = imageObject;
            providerUpdates.coverImage = imageUrl;
            // Preserve profile image
            providerUpdates.providerProfile.profileImage = existingProfileImage;
            if (existingProfileImage?.url) {
              providerUpdates.profileImage = existingProfileImage.url;
            }
          }

          await dispatch(updateUser(providerUpdates)).unwrap();

          // Update localStorage
          try {
            const userDataStr = localStorage.getItem('user_data');
            if (userDataStr) {
              const userData = JSON.parse(userDataStr);

              if (!userData.providerProfile) {
                userData.providerProfile = {};
              }

              if (type === 'profile') {
                userData.providerProfile.profileImage = imageObject;
                userData.profileImage = imageUrl;
                // Preserve cover image in localStorage
                if (existingCoverImage?.url) {
                  userData.providerProfile.coverImage = existingCoverImage;
                  userData.coverImage = existingCoverImage.url;
                }
              } else {
                userData.providerProfile.coverImage = imageObject;
                userData.coverImage = imageUrl;
                // Preserve profile image in localStorage
                if (existingProfileImage?.url) {
                  userData.providerProfile.profileImage = existingProfileImage;
                  userData.profileImage = existingProfileImage.url;
                }
              }

              localStorage.setItem('user_data', JSON.stringify(userData));
            }
          } catch (storageError) {
            console.error('Failed to update localStorage:', storageError);
          }

          setImageUploadSuccess(true);

          // Refresh profile data
          if (user?._id) {
            await dispatch(getProfile());
          }
        }
      } else {
        const errorMessage = result?.payload || result?.error?.message || 'Upload failed. Please try again.';
        setImageUploadError(errorMessage);

        // Revert preview on error - use profile first, then user
        if (type === 'profile') {
          const originalUrl = profile?.providerProfile?.profileImage?.url || 
                             user?.providerProfile?.profileImage?.url || 
                             user?.profileImage || 
                             null;
          setLocalProfileImage(originalUrl);
        } else {
          const originalUrl = profile?.providerProfile?.coverImage?.url || 
                             user?.providerProfile?.coverImage?.url || 
                             user?.coverImage || 
                             null;
          setLocalCoverImage(originalUrl);
        }
      }

    } catch (error) {
      console.error('Upload error:', error);
      setImageUploadError(error?.message || 'Upload failed. Please try again.');

      // Revert preview on error
      if (type === 'profile') {
        const originalUrl = profile?.providerProfile?.profileImage?.url || 
                           user?.providerProfile?.profileImage?.url || 
                           user?.profileImage || 
                           null;
        setLocalProfileImage(originalUrl);
      } else {
        const originalUrl = profile?.providerProfile?.coverImage?.url || 
                           user?.providerProfile?.coverImage?.url || 
                           user?.coverImage || 
                           null;
        setLocalCoverImage(originalUrl);
      }
    } finally {
      setUploadingImageType(null);
      isImageUploadInProgress.current = false;
      setTimeout(() => {
        isUpdatingFromUpload.current = false;
      }, 300);
    }
  };

  const handleRemoveImage = async (type) => {
    if (!window.confirm(deleteConfirm)) return;

    if (type === 'profile') {
      setLocalProfileImage(null);
    } else {
      setLocalCoverImage(null);
    }

    const currentProviderProfile = profile?.providerProfile || user?.providerProfile || {};

    const providerUpdates = {
      providerProfile: {
        ...currentProviderProfile,
      }
    };

    if (type === 'profile') {
      providerUpdates.providerProfile.profileImage = null;
      providerUpdates.profileImage = null;
    } else {
      providerUpdates.providerProfile.coverImage = null;
      providerUpdates.coverImage = null;
    }

    await dispatch(updateUser(providerUpdates));
    alert(deleteSuccessMessage);

    // Refresh profile data
    if (user?._id) {
      await dispatch(getProfile());
    }
  };

  const handleSaveImages = async () => {
    const hasProfileImage = localProfileImage && localProfileImage.startsWith('http');
    const hasCoverImage = localCoverImage && localCoverImage.startsWith('http');
    const hasUploadedImage = localProfileImage !== null || localCoverImage !== null;

    if (!hasUploadedImage) {
      setValidationMessage('Please upload at least one image (profile or cover) before continuing, or tap Skip to do it later.');
      setTimeout(() => setValidationMessage(null), 5000);
      return;
    }

    if (!hasProfileImage && !hasCoverImage) {
      setValidationMessage('Please wait for your image upload to complete before saving.');
      setTimeout(() => setValidationMessage(null), 5000);
      return;
    }

    setIsSaving(true);
    setValidationMessage(null);

    try {
      const currentProviderProfile = profile?.providerProfile || user?.providerProfile || {};

      const updates = {
        providerProfile: {
          ...currentProviderProfile,
        }
      };

      if (hasProfileImage) {
        updates.profileImage = localProfileImage;
        updates.providerProfile.profileImage = {
          ...(currentProviderProfile.profileImage || {}),
          url: localProfileImage
        };
      }

      if (hasCoverImage) {
        updates.coverImage = localCoverImage;
        updates.providerProfile.coverImage = {
          ...(currentProviderProfile.coverImage || {}),
          url: localCoverImage
        };
      }

      const result = await dispatch(updateUser(updates));
      if (result.error) {
        throw new Error(result.payload || 'Failed to save images');
      }

      // Update localStorage
      try {
        const userDataStr = localStorage.getItem('user_data');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);

          if (!userData.providerProfile) {
            userData.providerProfile = {};
          }

          if (hasProfileImage) {
            userData.providerProfile.profileImage = {
              ...(userData.providerProfile.profileImage || {}),
              url: localProfileImage
            };
            userData.profileImage = localProfileImage;
          }

          if (hasCoverImage) {
            userData.providerProfile.coverImage = {
              ...(userData.providerProfile.coverImage || {}),
              url: localCoverImage
            };
            userData.coverImage = localCoverImage;
          }

          localStorage.setItem('user_data', JSON.stringify(userData));
        }
      } catch (storageError) {
        console.error('Failed to update localStorage on save:', storageError);
      }

      // Refresh profile data
      if (user?._id) {
        await dispatch(getProfile());
      }

      alert('Images saved successfully!');
      navigate('/provider-gallery');

    } catch (error) {
      console.error('Save error:', error);
      setValidationMessage(error?.message || 'Failed to save images. Please try again.');
      setTimeout(() => setValidationMessage(null), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkipImages = () => {
    if (window.confirm('You can add images later from your profile settings. Are you sure you want to skip?')) {
      navigate('/provider-gallery');
    }
  };

  const renderProfileImageSection = () => {
    const hasImage = !!localProfileImage;
    const isUploadingThis = isImageUploading && uploadingImageType === 'profile';

    return (
      <div className={styles.imageSection}>
        <h3 className={styles.sectionTitle}>
          {profileImageTitle}
          <span className={styles.requiredIndicator}>*</span>
        </h3>
        <div className={styles.imageContainer}>
          <div className={styles.profileImageWrapper}>
            {hasImage ? (
              <img
                src={localProfileImage}
                alt="Profile"
                className={styles.profileImage}
                onError={() => {
                  // Fallback to profile or user image
                  const fallbackUrl = profile?.providerProfile?.profileImage?.url || 
                                     user?.providerProfile?.profileImage?.url || 
                                     user?.profileImage || 
                                     null;
                  if (fallbackUrl && fallbackUrl !== localProfileImage) {
                    setLocalProfileImage(fallbackUrl);
                  } else {
                    setLocalProfileImage(null);
                  }
                }}
              />
            ) : (
              <div className={styles.profileImagePlaceholder}>
                <User size={60} color={colors.gray400} />
                <span className={styles.placeholderText}>{uploadProfilePhoto}</span>
              </div>
            )}
            <button
              className={styles.changePhotoButton}
              onClick={() => handleSelectImage('profile')}
              disabled={isImageUploading}
            >
              <Camera size={20} color={colors.white} />
            </button>
            {isUploadingThis && (
              <div className={styles.uploadingOverlay}>
                <Loader2 size={30} className={styles.spinner} />
                <span className={styles.uploadingText}>
                  {uploading} {uploadProgress}%
                </span>
              </div>
            )}
          </div>
          <div className={styles.imageActions}>
            <button
              className={styles.imageActionButton}
              onClick={() => handleSelectImage('profile')}
              disabled={isImageUploading}
            >
              <CloudUpload size={20} />
              <span>{hasImage ? changePhoto : uploadProfilePhoto}</span>
            </button>
            {hasImage && (
              <button
                className={`${styles.imageActionButton} ${styles.imageActionButtonDanger}`}
                onClick={() => handleRemoveImage('profile')}
                disabled={isImageUploading}
              >
                <Trash2 size={20} />
                <span>{removePhoto}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCoverImageSection = () => {
    const hasImage = !!localCoverImage;
    const isUploadingThis = isImageUploading && uploadingImageType === 'cover';

    return (
      <div className={styles.imageSection}>
        <h3 className={styles.sectionTitle}>
          {coverImageTitle}
          <span className={styles.requiredIndicator}>*</span>
        </h3>
        <div className={styles.imageContainer}>
          <div className={styles.coverImageWrapper}>
            {hasImage ? (
              <img
                src={localCoverImage}
                alt="Cover"
                className={styles.coverImage}
                onError={() => {
                  const fallbackUrl = profile?.providerProfile?.coverImage?.url || 
                                     user?.providerProfile?.coverImage?.url || 
                                     user?.coverImage || 
                                     null;
                  if (fallbackUrl && fallbackUrl !== localCoverImage) {
                    setLocalCoverImage(fallbackUrl);
                  } else {
                    setLocalCoverImage(null);
                  }
                }}
              />
            ) : (
              <div className={styles.coverImagePlaceholder}>
                <ImageIcon size={60} color={colors.gray400} />
                <span className={styles.placeholderText}>{uploadCoverPhoto}</span>
              </div>
            )}
            <button
              className={styles.changeCoverButton}
              onClick={() => handleSelectImage('cover')}
              disabled={isImageUploading}
            >
              <Camera size={20} color={colors.white} />
            </button>
            {isUploadingThis && (
              <div className={styles.uploadingOverlay}>
                <Loader2 size={30} className={styles.spinner} />
                <span className={styles.uploadingText}>
                  {uploading} {uploadProgress}%
                </span>
              </div>
            )}
          </div>
          <div className={styles.imageActions}>
            <button
              className={styles.imageActionButton}
              onClick={() => handleSelectImage('cover')}
              disabled={isImageUploading}
            >
              <CloudUpload size={20} />
              <span>{hasImage ? changePhoto : uploadCoverPhoto}</span>
            </button>
            {hasImage && (
              <button
                className={`${styles.imageActionButton} ${styles.imageActionButtonDanger}`}
                onClick={() => handleRemoveImage('cover')}
                disabled={isImageUploading}
              >
                <Trash2 size={20} />
                <span>{removePhoto}</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderImageUploadModal = () => {
    if (!showImageOptionsModal) return null;

    return (
      <div className={styles.modalOverlay} onClick={() => setShowImageOptionsModal(false)}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h3 className={styles.modalTitle}>
              {uploadingImageType === 'profile' ? profileImageTitle : coverImageTitle}
            </h3>
            <button
              className={styles.modalClose}
              onClick={() => setShowImageOptionsModal(false)}
            >
              <X size={24} />
            </button>
          </div>

          <div className={styles.modalBody}>
            <label className={styles.modalOption}>
              <input
                ref={imageFileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => handleImageFileSelect(uploadingImageType, e)}
                className={styles.fileInput}
              />
              <ImageIcon size={24} className={styles.modalOptionIcon} />
              <span className={styles.modalOptionText}>{chooseFromGallery}</span>
            </label>
          </div>

          <button
            className={styles.modalCancelButton}
            onClick={() => setShowImageOptionsModal(false)}
          >
            {cancel}
          </button>
        </div>
      </div>
    );
  };

  const renderNotification = () => {
    if (validationMessage) {
      return (
        <div className={`${styles.notification} ${styles.notificationWarning}`}>
          <AlertCircle size={20} />
          <span>{validationMessage}</span>
        </div>
      );
    }

    if (imageUploadSuccess) {
      return (
        <div className={`${styles.notification} ${styles.notificationSuccess}`}>
          <CheckCircle size={20} />
          <span>{uploadSuccessMessage}</span>
        </div>
      );
    }

    if (imageUploadError) {
      return (
        <div className={`${styles.notification} ${styles.notificationError}`}>
          <AlertCircle size={20} />
          <span>{imageUploadError}</span>
        </div>
      );
    }

    return null;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <Loader2 size={40} className={styles.spinner} />
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  // If no providerId, render nothing (useEffect will redirect)
  if (!providerId) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Header */}
        <div className={styles.header}>
          <button
            className={styles.backButton}
            onClick={() => navigate(-1)}
            disabled={isSaving}
          >
            <ArrowLeft size={24} color={colors.textPrimary} />
          </button>
          <h2 className={styles.headerTitle}>Business Images</h2>
          <div className={styles.headerRight} />
        </div>

        {/* Notification */}
        {renderNotification()}

        <div className={styles.scrollContent}>
          <div className={styles.imageUploadSection}>
            <div className={styles.infoBanner}>
              <AlertCircle size={24} color={colors.primary} />
              <p className={styles.infoText}>
                Add a profile photo and cover image to help clients recognize your business.
                <span className={styles.infoHighlight}> Both images are recommended but you can proceed with at least one.</span>
              </p>
            </div>

            {renderProfileImageSection()}
            {renderCoverImageSection()}

            <div className={styles.imageActionButtonsContainer}>
              <button
                className={`${styles.imageActionButton} ${styles.skipButton}`}
                onClick={handleSkipImages}
                disabled={isSaving || isImageUploading}
              >
                {skip}
              </button>

              <button
                className={`${styles.imageActionButton} ${styles.saveButton} ${(isSaving || isImageUploading) ? styles.saveButtonDisabled : ''}`}
                onClick={handleSaveImages}
                disabled={isSaving || isImageUploading}
              >
                {isSaving ? (
                  <>
                    <Loader2 size={20} className={styles.spinner} />
                    <span>{saving}</span>
                  </>
                ) : (
                  <span>Save</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {renderImageUploadModal()}
    </div>
  );
};

export default ProfileImageProviderServicePage;